import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FileText, Search, Navigation } from 'lucide-react';
import { cspireAPI, serviceCallsAPI } from '@shared/apiClient';
import { getDirectionsUrl } from './DetailSection';
import toast from 'react-hot-toast';
import CSpireSiteDetailsModal from './CSpireSiteDetailsModal';
import ServiceCallModal from './ServiceCallModal';

export default function CSpireSitesTab() {
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedServiceCall, setSelectedServiceCall] = useState(null);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading } = useQuery(
    ['cspireSites', searchTerm],
    () => cspireAPI.getSites({ search: searchTerm || undefined }),
    { keepPreviousData: true }
  );

  const createServiceCallMutation = useMutation(
    (cspireSiteId) => serviceCallsAPI.createTechnicianServiceCall({ cspireSiteId }),
    {
      onSuccess: (response) => {
        const createdServiceCall = response?.data?.serviceCall;
        if (!createdServiceCall) {
          toast.error('Service call created, but no details returned.');
          return;
        }
        setSelectedServiceCall(createdServiceCall);
        setIsServiceModalOpen(true);
        toast.success('Service call started');
        queryClient.invalidateQueries('weeklyAssignments');
        queryClient.invalidateQueries('priorityAssignments');
        queryClient.invalidateQueries('myServiceCalls');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to create service call');
      }
    }
  );

  const sites = data?.data?.sites || [];

  const getSiteLocation = (site) => {
    const parts = [site.city, site.state].filter(Boolean).join(', ');
    return parts || site.addressLine1 || '—';
  };

  return (
    <>
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search C Spire sites..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="text-center py-10 text-gray-500">Loading sites...</div>
        ) : sites.length === 0 ? (
          <div className="text-center py-10 text-gray-500">No C Spire sites found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Site ID
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.map((site) => (
                  <tr key={site.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {site.siteId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {site.locationName || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {getSiteLocation(site)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-4">
                        {(() => {
                          const dirUrl = getDirectionsUrl({ latitude: site.latitude, longitude: site.longitude, address: site.addressLine1, city: site.city, state: site.state, zipCode: site.zipCode });
                          return dirUrl ? (
                            <a
                              href={dirUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800"
                            >
                              <Navigation className="h-4 w-4 mr-1" />
                              Directions
                            </a>
                          ) : null;
                        })()}
                        <button
                          onClick={() => {
                            setSelectedSite(site);
                            setIsDetailsOpen(true);
                          }}
                          className="text-primary-600 hover:text-primary-900 font-medium"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => createServiceCallMutation.mutate(site.id)}
                          disabled={createServiceCallMutation.isLoading}
                          className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Service Call
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CSpireSiteDetailsModal
        site={selectedSite}
        isOpen={isDetailsOpen}
        onClose={() => {
          setIsDetailsOpen(false);
          setSelectedSite(null);
        }}
      />

      {selectedServiceCall && (
        <ServiceCallModal
          serviceCall={selectedServiceCall}
          isOpen={isServiceModalOpen}
          onClose={() => {
            setIsServiceModalOpen(false);
            setSelectedServiceCall(null);
          }}
        />
      )}
    </>
  );
}
